import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { api, ApiRequestError } from '../../lib/api';
import {
  contentKindLabel,
  defaultPlaylistDurationSeconds,
  formatMediaByteSize,
} from './buildContentLibrary';
import { ContentLibraryLightboxTitle } from './ContentLibraryLightboxTitle';
import { getMediaPreviewPlayUrl } from './mediaPreview';
import { isMediaUsableInPlaylist } from './mediaOperatorStatus';
import type { PlaylistMediaMembership } from './usePlaylistMediaUsage';

type ContentLibraryLightboxProps = {
  organisationId: string;
  asset: MediaAsset;
  assets: MediaAsset[];
  membership: PlaylistMediaMembership[];
  onClose: () => void;
  onSelect: (assetId: string) => void;
  onRemoved: () => void;
};

export function ContentLibraryLightbox({
  organisationId,
  asset,
  assets,
  membership,
  onClose,
  onSelect,
  onRemoved,
}: ContentLibraryLightboxProps) {
  const { t } = useTranslation(['content', 'common']);
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const url = getMediaPreviewPlayUrl(asset);
  const index = assets.findIndex((item) => item.id === asset.id);
  const position = t('lightbox.position', {
    index: index >= 0 ? index + 1 : 1,
    total: Math.max(assets.length, 1),
  });
  const canUse = isMediaUsableInPlaylist(asset);

  const step = (dir: -1 | 1) => {
    if (assets.length === 0) return;
    const current = index >= 0 ? index : 0;
    const next = (current + dir + assets.length) % assets.length;
    const target = assets[next];
    if (target) onSelect(target.id);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement) {
        const tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      }
      if (event.key === 'Escape') onClose();
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      if (assets.length === 0) return;
      // Arrows follow reading direction: in right-to-left, ArrowLeft is "next".
      const nextKey = document.documentElement.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
      const offset = event.key === nextKey ? 1 : -1;
      const current = assets.findIndex((item) => item.id === asset.id);
      const base = current >= 0 ? current : 0;
      const target = assets[(base + offset + assets.length) % assets.length];
      if (target) onSelect(target.id);
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [asset.id, assets, onClose, onSelect]);

  const invalidatePlaylists = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] }),
      ...membership.map((row) =>
        queryClient.invalidateQueries({ queryKey: ['playlist-items', row.playlistId] }),
      ),
    ]);
  };

  const togglePlaylist = useMutation({
    mutationFn: async (row: PlaylistMediaMembership) => {
      if (row.itemId) {
        await api.removePlaylistItem(organisationId, row.playlistId, row.itemId);
        return;
      }
      if (!canUse) {
        throw new Error(t('lightbox.notReady'));
      }
      await api.addPlaylistItem(organisationId, row.playlistId, {
        mediaAssetId: asset.id,
        durationSeconds: defaultPlaylistDurationSeconds(asset),
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await invalidatePlaylists();
    },
    onError: (err) => {
      setActionError(
        err instanceof Error && !(err instanceof ApiRequestError)
          ? err.message
          : apiErrorMessage(err, t('lightbox.toggleError')),
      );
    },
  });

  const removeAsset = useMutation({
    mutationFn: () => api.deleteMediaAsset(organisationId, asset.id),
    onSuccess: async () => {
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: ['media', organisationId] });
      await invalidatePlaylists();
      onRemoved();
    },
    onError: (err) => {
      setActionError(apiErrorMessage(err, t('lightbox.removeError')));
    },
  });

  // Portal: the page wrapper's entrance animation would otherwise trap `fixed` inside it.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('previewLabel', { name: asset.originalFilename })}
      className="fixed inset-0 z-[60] flex flex-col text-[#F2F1EE]"
      style={{
        background: 'rgba(6,6,8,0.95)',
        animation: 'gs-in 160ms ease both',
      }}
    >
      <div className="flex items-center gap-3.5 px-[22px] py-4">
        <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
          <ContentLibraryLightboxTitle
            organisationId={organisationId}
            asset={asset}
            onError={setActionError}
          />
          <span className="text-[13px] text-[#A3A29D]">
            {t('lightbox.meta', {
              kind: contentKindLabel(asset.kind),
              size: formatMediaByteSize(asset.byteSize),
              position,
            })}
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-[10px] border border-white/18 bg-white/12 px-[18px] py-[9px] text-sm font-bold text-white transition hover:bg-white/20"
        >
          {t('common:actions.close')}
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 sm:px-[84px]">
        <button
          type="button"
          title={t('lightbox.previous')}
          onClick={() => step(-1)}
          disabled={assets.length < 2}
          className="absolute start-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border-0 bg-white/12 text-white transition hover:bg-white/22 disabled:opacity-30 sm:start-5 sm:h-[46px] sm:w-[46px]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="rtl:-scale-x-100" aria-hidden>
            <path d="m15 6-6 6 6 6" />
          </svg>
        </button>

        <div
          className="flex h-full w-full items-center justify-center py-2"
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          {url && asset.kind === 'video' ? (
            <video
              key={asset.id}
              src={url}
              controls
              autoPlay
              className="max-h-full max-w-full"
            />
          ) : url ? (
            <img
              key={asset.id}
              src={url}
              alt={asset.originalFilename}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="flex aspect-video w-full max-w-[800px] flex-col items-center justify-center gap-2.5 rounded-lg bg-[#1b1b1f]">
              <span className="font-mono text-xs uppercase tracking-[0.14em] text-[#A3A29D]">
                {contentKindLabel(asset.kind)}
              </span>
              <span className="text-xl font-bold">{asset.originalFilename}</span>
            </span>
          )}
        </div>

        <button
          type="button"
          title={t('lightbox.next')}
          onClick={() => step(1)}
          disabled={assets.length < 2}
          className="absolute end-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border-0 bg-white/12 text-white transition hover:bg-white/22 disabled:opacity-30 sm:end-5 sm:h-[46px] sm:w-[46px]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="rtl:-scale-x-100" aria-hidden>
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-white/10 px-[22px] py-4 pb-5">
        <span className="me-1 text-[13.5px] font-bold text-[#CFCDC8]">
          {t('lightbox.showInPlaylist')}
        </span>
        {membership.length === 0 ? (
          <span className="text-[13.5px] text-[#A3A29D]">{t('lightbox.noPlaylists')}</span>
        ) : (
          membership.map((row) => {
            const on = Boolean(row.itemId);
            return (
              <button
                key={row.playlistId}
                type="button"
                disabled={togglePlaylist.isPending || (!on && !canUse)}
                onClick={() => togglePlaylist.mutate(row)}
                className="inline-flex items-center gap-[7px] rounded-full border-[1.5px] px-[13px] py-[7px] text-[13.5px] font-semibold disabled:opacity-45"
                style={{
                  color: on ? '#FFFFFF' : '#CFCDC8',
                  background: on ? 'rgba(228,118,58,0.18)' : 'transparent',
                  borderColor: on ? '#E4763A' : 'rgba(255,255,255,0.2)',
                }}
              >
                <span className="font-extrabold text-[#E4763A]">{on ? '✓' : '+'}</span>
                {row.playlistName}
              </button>
            );
          })
        )}

        <button
          type="button"
          disabled={removeAsset.isPending}
          onClick={() => removeAsset.mutate()}
          className="ms-auto rounded-[10px] border border-[rgba(240,96,63,0.5)] bg-transparent px-4 py-[9px] text-sm font-bold text-[#F0603F] transition hover:border-[#F0603F] disabled:opacity-50"
        >
          {removeAsset.isPending ? t('removing') : t('common:actions.remove')}
        </button>

        {actionError ? (
          <span className="w-full text-sm text-[#F0603F]" role="alert">
            {actionError}
          </span>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
